import React, { useState, useEffect, useMemo, useRef } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { ChevronLeft, ChevronRight, ExternalLink, Info, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { cn, formatDateToString, formatUTCDateToString } from "@/lib/utils";
import type { ChildBase, BookablePeriodBase, PeriodEnum } from "nautika-types";

// Helper pour formater les noms avec "et" à la française
function formatChildNamesWithEt(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} et ${names[1]}`;
  const allButLast = names.slice(0, -1).join(", ");
  return `${allButLast} et ${names[names.length - 1]}`;
}

interface PeriodEditorProps {
  selectedChildren: ChildBase[];
  onPeriodsChange: (periods: { [childId: string]: string[] }) => void;
  initialSelectedPeriods?: { [childId: string]: string[] };
  registrationId?: string; // fiche courante à exclure des blocages
  isInitialLoading?: boolean; // indique si les données initiales sont en cours de chargement
}

interface DayPeriods {
  [key: string]: BookablePeriodBase[];
}

export const PeriodEditor = function PeriodEditor({
  selectedChildren,
  onPeriodsChange,
  initialSelectedPeriods = {},
  registrationId,
  isInitialLoading = false,
}: PeriodEditorProps) {
  // Calculer si les périodes initiales sont identiques pour tous les enfants
  const initialIdenticalForAll = useMemo(() => {
    const childIds = selectedChildren.map((c) => c.id);
    if (childIds.length <= 1) return true;

    // Récupérer les périodes du premier enfant
    const firstChildPeriods = initialSelectedPeriods[childIds[0]] || [];
    const firstChildPeriodsSorted = [...firstChildPeriods].sort().join(",");

    // Vérifier si tous les autres enfants ont les mêmes périodes
    for (let i = 1; i < childIds.length; i++) {
      const childPeriods = initialSelectedPeriods[childIds[i]] || [];
      const childPeriodsSorted = [...childPeriods].sort().join(",");
      if (childPeriodsSorted !== firstChildPeriodsSorted) {
        return false;
      }
    }
    return true;
  }, [selectedChildren, initialSelectedPeriods]);

  // Utiliser null pour indiquer que l'utilisateur n'a pas encore fait de choix
  const [identicalForAllOverride, setIdenticalForAllOverride] = useState<boolean | null>(null);
  // Valeur effective: priorité au choix utilisateur, sinon valeur calculée des données initiales
  const identicalForAll = identicalForAllOverride ?? initialIdenticalForAll;
  const setIdenticalForAll = setIdenticalForAllOverride;

  const [selectedPeriods, setSelectedPeriods] = useState<{ [childId: string]: string[] }>(initialSelectedPeriods);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // Track last emitted map to parent to avoid redundant updates causing loops
  const lastEmittedRef = useRef<string>("");

  // Derived map that merges internal changes with initial periods (no setState effects)
  const derivedSelectedPeriods = useMemo(() => {
    const merged: { [childId: string]: string[] } = {};
    selectedChildren.forEach((child) => {
      merged[child.id] = selectedPeriods[child.id] ?? initialSelectedPeriods[child.id] ?? [];
    });
    return merged;
  }, [selectedChildren, selectedPeriods, initialSelectedPeriods]);

  // Générer les mois à charger (mois précédent, actuel, et suivant)
  const monthsToLoad = React.useMemo(() => {
    const months = [];

    // Mois précédent
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const prevMonthStr = `${prevMonth.getFullYear()}-${(prevMonth.getMonth() + 1).toString().padStart(2, "0")}`;
    months.push(prevMonthStr);

    // Mois actuel
    const currentMonthStr = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, "0")}`;
    months.push(currentMonthStr);

    // Mois suivant
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    const nextMonthStr = `${nextMonth.getFullYear()}-${(nextMonth.getMonth() + 1).toString().padStart(2, "0")}`;
    months.push(nextMonthStr);

    return months;
  }, [currentMonth]);

  // Charger les périodes réservables pour les 3 mois
  const prevMonthQuery = api.useQuery("get", "/club/bookable-periods", {
    params: {
      query: { month: monthsToLoad[0] },
    },
    staleTime: 300000, // 5 minutes
  });

  const currentMonthQuery = api.useQuery("get", "/club/bookable-periods", {
    params: {
      query: { month: monthsToLoad[1] },
    },
    staleTime: 300000, // 5 minutes
  });

  const nextMonthQuery = api.useQuery("get", "/club/bookable-periods", {
    params: {
      query: { month: monthsToLoad[2] },
    },
    staleTime: 300000, // 5 minutes
  });

  // Combiner toutes les données des 3 mois
  const allPeriodsData = React.useMemo(() => {
    const allPeriods: BookablePeriodBase[] = [];

    [prevMonthQuery, currentMonthQuery, nextMonthQuery].forEach((query) => {
      if (query.data && Array.isArray(query.data)) {
        allPeriods.push(...(query.data as BookablePeriodBase[]));
      }
    });

    return allPeriods;
  }, [prevMonthQuery, currentMonthQuery, nextMonthQuery]); // Vérifier si au moins une requête est en cours de chargement
  const isLoading = prevMonthQuery.isLoading || currentMonthQuery.isLoading || nextMonthQuery.isLoading;

  // --- Périodes déjà réservées (blocages) ---
  const childIdsCsv = React.useMemo(
    () =>
      selectedChildren
        .map((c) => c.id)
        .sort()
        .join(","),
    [selectedChildren]
  );
  const prevReservedQuery = api.useQuery("get", "/registrations/reserved-periods", {
    params: { query: { month: monthsToLoad[0], childIds: childIdsCsv } },
    enabled: selectedChildren.length > 0,
    staleTime: 300000,
  });
  const currentReservedQuery = api.useQuery("get", "/registrations/reserved-periods", {
    params: { query: { month: monthsToLoad[1], childIds: childIdsCsv } },
    enabled: selectedChildren.length > 0,
    staleTime: 300000,
  });
  const nextReservedQuery = api.useQuery("get", "/registrations/reserved-periods", {
    params: { query: { month: monthsToLoad[2], childIds: childIdsCsv } },
    enabled: selectedChildren.length > 0,
    staleTime: 300000,
  });

  type ReservedLookup = { [childId: string]: Map<string, string> }; // periodId -> registrationId
  interface ReservedPeriodItem {
    childId: string;
    periodId: string;
    registrationId: string;
  }
  const reservedLookup: ReservedLookup = React.useMemo(() => {
    const map: ReservedLookup = {};
    const datasets: (ReservedPeriodItem[] | undefined)[] = [
      prevReservedQuery.data as ReservedPeriodItem[] | undefined,
      currentReservedQuery.data as ReservedPeriodItem[] | undefined,
      nextReservedQuery.data as ReservedPeriodItem[] | undefined,
    ];
    for (const raw of datasets) {
      if (!raw) continue;
      for (const r of raw) {
        // Exclure les réservations provenant de la fiche en cours d'édition
        if (registrationId && r.registrationId === registrationId) continue;
        if (!map[r.childId]) map[r.childId] = new Map();
        map[r.childId].set(r.periodId, r.registrationId);
      }
    }
    return map;
  }, [prevReservedQuery.data, currentReservedQuery.data, nextReservedQuery.data, registrationId]);
  const reservedLoading = prevReservedQuery.isLoading || currentReservedQuery.isLoading || nextReservedQuery.isLoading;

  // Organiser les périodes par jour
  const periodsByDay: DayPeriods = React.useMemo(() => {
    const dayPeriods: DayPeriods = {};

    allPeriodsData.forEach((period: BookablePeriodBase) => {
      // Utiliser la date UTC directement pour éviter les problèmes de timezone
      const utcDate = new Date(period.startTime);
      const date = formatUTCDateToString(utcDate);
      if (!dayPeriods[date]) {
        dayPeriods[date] = [];
      }
      dayPeriods[date].push(period);
    });

    return dayPeriods;
  }, [allPeriodsData]);

  // Note: pas d'effet d'initialisation de state; on normalise la map à l'envoi

  // Notifier les changements (en évitant les boucles de mise à jour)
  useEffect(() => {
    // Appliquer l'option identique pour tous si nécessaire
    const payload = (() => {
      if (identicalForAll && selectedChildren.length > 0) {
        const firstChildId = selectedChildren[0].id;
        const firstChildPeriods = derivedSelectedPeriods[firstChildId] || [];
        const unified: { [childId: string]: string[] } = {};
        selectedChildren.forEach((child) => {
          unified[child.id] = [...firstChildPeriods];
        });
        return unified;
      }
      return derivedSelectedPeriods;
    })();

    // Comparer avec la dernière valeur émise
    const nextKey = JSON.stringify(payload);
    if (nextKey !== lastEmittedRef.current) {
      lastEmittedRef.current = nextKey;
      onPeriodsChange(payload);
    }
  }, [derivedSelectedPeriods, identicalForAll, selectedChildren, onPeriodsChange]);

  // Gérer la sélection d'une période individuelle
  const handlePeriodToggle = (childId: string, periodId: string) => {
    const blocked = identicalForAll ? selectedChildren.some((c) => reservedLookup[c.id]?.has(periodId)) : !!reservedLookup[childId]?.has(periodId);
    if (blocked) return;
    setSelectedPeriods((prev) => {
      const newPeriods = { ...prev };
      const baseForChild = (prev[childId] ?? initialSelectedPeriods[childId] ?? []) as string[];
      const childPeriods = [...baseForChild];

      if (childPeriods.includes(periodId)) {
        newPeriods[childId] = childPeriods.filter((id) => id !== periodId);
      } else {
        newPeriods[childId] = [...childPeriods, periodId];
      }

      // Si identique pour tous, mettre à jour tous les enfants
      if (identicalForAll) {
        selectedChildren.forEach((child) => {
          newPeriods[child.id] = [...newPeriods[childId]];
        });
      }

      return newPeriods;
    });
  };

  // Gérer la sélection d'une semaine entière pour une période
  const handleWeekToggle = (childId: string, periodType: PeriodEnum, weekStart: Date) => {
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      weekDays.push(formatDateToString(day));
    }

    // Trouver toutes les périodes de ce type dans la semaine
    const weekPeriods: string[] = [];
    weekDays.forEach((dateStr) => {
      const dayPeriods = periodsByDay[dateStr] || [];
      dayPeriods.forEach((period) => {
        if (period.period === periodType) {
          weekPeriods.push(period.id);
        }
      });
    });

    setSelectedPeriods((prev) => {
      const newPeriods = { ...prev };
      const baseForChild = (prev[childId] ?? initialSelectedPeriods[childId] ?? []) as string[];
      const childPeriods = [...baseForChild];

      // Vérifier si tous les périodes de la semaine sont déjà sélectionnées
      const allSelected = weekPeriods.every((id) => childPeriods.includes(id));

      if (allSelected) {
        // Désélectionner toutes les périodes de la semaine
        newPeriods[childId] = childPeriods.filter((id) => !weekPeriods.includes(id));
      } else {
        // Sélectionner toutes les périodes de la semaine
        const uniquePeriods = [...new Set([...childPeriods, ...weekPeriods])];
        newPeriods[childId] = uniquePeriods;
      }

      // Si identique pour tous, mettre à jour tous les enfants
      if (identicalForAll) {
        selectedChildren.forEach((child) => {
          newPeriods[child.id] = [...newPeriods[childId]];
        });
      }

      return newPeriods;
    });
  };

  // Vérifier si une période est sélectionnée
  const isPeriodSelected = (childId: string, periodId: string) => {
    return derivedSelectedPeriods[childId]?.includes(periodId) || false;
  };

  // Vérifier si toute une semaine est sélectionnée pour un type de période
  const isWeekSelected = (childId: string, periodType: PeriodEnum, weekStart: Date) => {
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      weekDays.push(formatDateToString(day));
    }

    const weekPeriods: string[] = [];
    weekDays.forEach((dateStr) => {
      const dayPeriods = periodsByDay[dateStr] || [];
      dayPeriods.forEach((period) => {
        if (period.period === periodType) {
          weekPeriods.push(period.id);
        }
      });
    });

    const childPeriods = derivedSelectedPeriods[childId] || [];
    return weekPeriods.length > 0 && weekPeriods.every((id) => childPeriods.includes(id));
  };

  // Réinitialiser la sélection
  const handleResetSelection = (childId: string) => {
    setSelectedPeriods((prev) => {
      const newPeriods = { ...prev };

      if (identicalForAll) {
        // Si identique pour tous, réinitialiser tous les enfants
        selectedChildren.forEach((child) => {
          newPeriods[child.id] = [];
        });
      } else {
        // Sinon, réinitialiser seulement cet enfant
        newPeriods[childId] = [];
      }

      return newPeriods;
    });
  };

  // Obtenir le début de la semaine
  const getWeekStart = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Lundi = début de semaine
    start.setDate(diff);
    return start;
  };

  // Vérifier si le mois actuel contient des périodes bookables
  const hasBookablePeriodsInMonth = React.useMemo(() => {
    // Utiliser une méthode cohérente pour formater le mois (éviter les problèmes de timezone)
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1; // getMonth() retourne 0-11
    const currentMonthStr = `${year}-${month.toString().padStart(2, "0")}`; // YYYY-MM

    // Filtrer les périodes pour le mois actuel uniquement
    const currentMonthPeriods = allPeriodsData.filter((period) => {
      const periodDate = new Date(period.startTime);
      const periodYear = periodDate.getFullYear();
      const periodMonth = periodDate.getMonth() + 1;
      const periodMonthStr = `${periodYear}-${periodMonth.toString().padStart(2, "0")}`;
      return periodMonthStr === currentMonthStr;
    });

    return currentMonthPeriods.length > 0;
  }, [allPeriodsData, currentMonth]);

  // Vérifier si les données du mois actuel sont déjà chargées
  const currentMonthDataLoaded = React.useMemo(() => {
    const currentMonthStr = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, "0")}`;

    // Déterminer quelle requête correspond au mois actuel
    const currentMonthIndex = monthsToLoad.findIndex((month) => month === currentMonthStr);

    if (currentMonthIndex === -1) {
      // Le mois actuel n'est pas dans les mois chargés, on doit attendre
      return false;
    }

    // Vérifier si la requête correspondante est terminée
    const queries = [prevMonthQuery, currentMonthQuery, nextMonthQuery];
    const relevantQuery = queries[currentMonthIndex];

    return relevantQuery && !relevantQuery.isLoading;
  }, [currentMonth, monthsToLoad, prevMonthQuery, currentMonthQuery, nextMonthQuery]);

  // Nettoyage sélection identicalForAll (enfants ajoutés ayant blocages)
  useEffect(() => {
    if (!identicalForAll) return;
    if (selectedChildren.length === 0) return;
    const firstChildId = selectedChildren[0].id;
    const base = derivedSelectedPeriods[firstChildId] || [];
    const filtered = base.filter((pid) => !selectedChildren.some((c) => reservedLookup[c.id]?.has(pid)));
    if (filtered.length !== base.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedPeriods((prev) => {
        const next = { ...prev };
        selectedChildren.forEach((c) => {
          next[c.id] = filtered;
        });
        return next;
      });
    }
  }, [identicalForAll, selectedChildren, reservedLookup, derivedSelectedPeriods]);

  // Calendrier déporté en composant de niveau supérieur pour satisfaire le compilateur React

  if (selectedChildren.length === 0) {
    return (
      <div className="flex flex-col gap-y-2">
        <div className="text-lg font-semibold">Dates</div>
        <div className="text-sm text-gray-700">Sélectionnez les dates demandées pour l'inscription</div>

        {/* Calendrier grisé avec overlay */}
        <div className="relative w-full overflow-x-auto">
          <div className="space-y-4 rounded-lg border p-4 opacity-50">
            {/* Navigation du mois grisée */}
            <div className="relative">
              <div className="flex items-center justify-center gap-4">
                <Button variant="outline" size="sm" disabled>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h3 className="w-54 text-center text-lg font-semibold capitalize">
                  {currentMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                </h3>
                <Button variant="outline" size="sm" disabled>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Calendrier simulé grisé */}
            <div className="grid min-w-[1000px] grid-cols-8 gap-1">
              <div className="p-2 text-center text-sm font-medium">Semaine</div>
              {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((day) => (
                <div key={day} className="p-2 text-center text-sm font-medium">
                  {day}
                </div>
              ))}
              {/* Quelques cellules d'exemple pour l'aperçu */}
              {Array.from({ length: 35 }, (_, i) => (
                <div key={i} className="h-[130px] rounded border bg-gray-50 p-2">
                  <div className="mb-2 text-center text-sm font-medium">{(i % 30) + 1}</div>
                  <div className="flex h-[calc(100%-2rem)] flex-col justify-center gap-1">
                    <div className="h-6 rounded border bg-gray-100"></div>
                    <div className="h-6 rounded border bg-gray-100"></div>
                    <div className="h-6 rounded border bg-gray-100"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Overlay avec message */}
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-xs">
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-lg">
              <p className="text-lg font-medium text-gray-600">Veuillez d'abord sélectionner des enfants pour choisir les dates</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentChildId = identicalForAll ? selectedChildren[0].id : selectedChildren[0].id;

  return (
    <div className="flex flex-col gap-y-2">
      <div className="text-lg font-semibold">Dates</div>
      <div className="text-sm text-gray-700">Sélectionnez les dates demandées pour l'inscription</div>

      {selectedChildren.length > 1 && (
        <div className="mb-6 flex items-center space-x-2 hover:cursor-pointer">
          {isInitialLoading ? (
            <>
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-4 w-64" />
            </>
          ) : (
            <>
              <Checkbox id="identical-for-all" checked={identicalForAll} onCheckedChange={(checked) => setIdenticalForAll(checked === true)} />
              <label
                htmlFor="identical-for-all"
                className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70 hover:cursor-pointer"
              >
                Périodes identiques pour tous les enfants
              </label>
            </>
          )}
        </div>
      )}

      <div className="w-full overflow-x-auto">
        {!identicalForAll && selectedChildren.length > 1 ? (
          <Tabs defaultValue={selectedChildren[0].id} className="w-full">
            <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${selectedChildren.length}, 1fr)` }}>
              {selectedChildren.map((child) => (
                <TabsTrigger key={child.id} value={child.id} className="text-xs">
                  {child.firstName} {child.lastName}
                </TabsTrigger>
              ))}
            </TabsList>

            {selectedChildren.map((child) => (
              <TabsContent key={child.id} value={child.id}>
                <CustomCalendar
                  childId={child.id}
                  currentMonth={currentMonth}
                  onChangeMonth={setCurrentMonth}
                  periodsByDay={periodsByDay}
                  isLoading={isLoading || reservedLoading || isInitialLoading}
                  reservedLookup={reservedLookup}
                  selectedChildren={selectedChildren}
                  identicalForAll={identicalForAll}
                  getWeekStart={getWeekStart}
                  isWeekSelected={isWeekSelected}
                  handleWeekToggle={handleWeekToggle}
                  isPeriodSelected={isPeriodSelected}
                  handlePeriodToggle={handlePeriodToggle}
                  handleResetSelection={handleResetSelection}
                  hasBookablePeriodsInMonth={hasBookablePeriodsInMonth}
                  currentMonthDataLoaded={currentMonthDataLoaded}
                />
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <CustomCalendar
            childId={currentChildId}
            currentMonth={currentMonth}
            onChangeMonth={setCurrentMonth}
            periodsByDay={periodsByDay}
            isLoading={isLoading || reservedLoading || isInitialLoading}
            reservedLookup={reservedLookup}
            selectedChildren={selectedChildren}
            identicalForAll={identicalForAll}
            getWeekStart={getWeekStart}
            isWeekSelected={isWeekSelected}
            handleWeekToggle={handleWeekToggle}
            isPeriodSelected={isPeriodSelected}
            handlePeriodToggle={handlePeriodToggle}
            handleResetSelection={handleResetSelection}
            hasBookablePeriodsInMonth={hasBookablePeriodsInMonth}
            currentMonthDataLoaded={currentMonthDataLoaded}
          />
        )}
      </div>
    </div>
  );
};

// Déclarer le calendrier en dehors du render pour éviter les resets d'état
const CustomCalendar: React.FC<{
  childId: string;
  currentMonth: Date;
  onChangeMonth: (newMonth: Date) => void;
  periodsByDay: DayPeriods;
  isLoading: boolean;
  reservedLookup: { [childId: string]: Map<string, string> };
  selectedChildren: ChildBase[];
  identicalForAll: boolean;
  getWeekStart: (date: Date) => Date;
  isWeekSelected: (childId: string, periodType: PeriodEnum, weekStart: Date) => boolean;
  handleWeekToggle: (childId: string, periodType: PeriodEnum, weekStart: Date) => void;
  isPeriodSelected: (childId: string, periodId: string) => boolean;
  handlePeriodToggle: (childId: string, periodId: string) => void;
  handleResetSelection: (childId: string) => void;
  hasBookablePeriodsInMonth: boolean;
  currentMonthDataLoaded: boolean;
}> = ({
  childId,
  currentMonth,
  onChangeMonth,
  periodsByDay,
  isLoading,
  reservedLookup,
  selectedChildren,
  identicalForAll,
  getWeekStart,
  isWeekSelected,
  handleWeekToggle,
  isPeriodSelected,
  handlePeriodToggle,
  handleResetSelection,
  hasBookablePeriodsInMonth,
  currentMonthDataLoaded,
}) => {
  const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

  const weeks: Date[][] = [];
  const firstDay = new Date(monthStart);
  firstDay.setDate(firstDay.getDate() - firstDay.getDay() + 1); // Début de la semaine (Lundi)

  const currentDate = new Date(firstDay);

  while (currentDate <= monthEnd || currentDate.getMonth() === monthStart.getMonth()) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    weeks.push(week);

    if (currentDate.getMonth() !== monthStart.getMonth() && currentDate.getDate() > 7) {
      break;
    }
  }

  const handleMonthChange = (newMonth: Date) => {
    onChangeMonth(newMonth);
  };

  return (
    <div id="period-calendar" className="space-y-4 rounded-lg border p-4">
      {/* Navigation du mois */}
      <div className="relative">
        <div id="month-navigation" className="flex items-center justify-center gap-4">
          <Button variant="outline" size="sm" onClick={() => handleMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="w-54 text-center text-lg font-semibold capitalize">{currentMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</h3>
          <Button variant="outline" size="sm" onClick={() => handleMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Légende des couleurs en haut à gauche */}
        <div className="absolute top-0 left-0 flex items-center gap-3 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded border border-green-300 bg-green-100"></div>
            <span>Sélectionné</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded border border-gray-300 bg-gray-100"></div>
            <span>Non sélectionné</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded border border-red-300 bg-red-50"></div>
            <span>Indisponible</span>
          </div>
        </div>

        {/* Bouton de réinitialisation fixé en haut à droite */}
        <div className="absolute top-0 right-0">
          <Button variant="outline" size="sm" onClick={() => handleResetSelection(childId)} className="text-xs">
            <RotateCcw className="mr-1 h-3 w-3" />
            Réinitialiser
          </Button>
        </div>
      </div>

      {/* Calendrier */}
      <div className="relative w-full overflow-x-auto">
        {/* Overlay pour les mois sans périodes bookables */}
        {currentMonthDataLoaded && !hasBookablePeriodsInMonth && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-xs">
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-lg">
              <p className="text-lg font-medium text-gray-600">
                Aucune période n'est réservable en{" "}
                <span className="font-semibold capitalize">{currentMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</span>
              </p>
            </div>
          </div>
        )}

        <div className="grid min-w-[1000px] grid-cols-8 gap-1">
          {/* En-tête */}
          <div className="flex items-center justify-center gap-1 p-2 text-center text-sm font-medium">
            <span>Semaine</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 cursor-help text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex flex-col items-center gap-1">
                    <p>Cliquez sur ces boutons pour sélectionner</p>
                    <p> toute une semaine d'un coup</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((day) => (
            <div key={day} className="p-2 text-center text-sm font-medium">
              {day}
            </div>
          ))}

          {/* Semaines */}
          {weeks.map((week, weekIndex) => {
            const weekStart = getWeekStart(week[0]);

            return (
              <React.Fragment key={weekIndex}>
                {/* Boutons de semaine dans une fausse case invisible */}
                <div className="h-[130px] p-2">
                  <div className="invisible mb-2 text-center text-sm font-medium">Semaine</div>

                  {/* Conteneur avec hauteur fixe et grille pour l'alignement */}
                  <div className="flex h-[calc(100%-2rem)] flex-col justify-center gap-1">
                    {(["matin", "midi", "apres-midi"] as PeriodEnum[]).map((periodType) => {
                      // Vérifier si des périodes de ce type existent dans la semaine
                      const weekDays: string[] = [];
                      for (let i = 0; i < 7; i++) {
                        const day = new Date(weekStart);
                        day.setDate(weekStart.getDate() + i);
                        weekDays.push(formatDateToString(day));
                      }

                      const hasPeriodsInWeek = weekDays.some((dateStr) => {
                        const dayPeriods = periodsByDay[dateStr] || [];
                        return dayPeriods.some((period) => period.period === periodType);
                      });

                      if (!hasPeriodsInWeek) {
                        // Élément fantôme invisible pour maintenir l'alignement
                        return <div key={periodType} className="invisible h-6"></div>;
                      }

                      const selected = isWeekSelected(childId, periodType, weekStart);
                      return (
                        <Button
                          key={periodType}
                          onClick={() => handleWeekToggle(childId, periodType, weekStart)}
                          variant={selected ? "default" : "outline"}
                          size="sm"
                          className={cn("h-6 px-2 py-1 text-xs", selected && "border-green-300 bg-green-100 text-green-700 hover:bg-green-200")}
                        >
                          {periodType === "matin" && "Matin"}
                          {periodType === "midi" && "Midi"}
                          {periodType === "apres-midi" && "Aprèm"}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Jours de la semaine */}
                {week.map((day, dayIndex) => {
                  const dateStr = formatDateToString(day);
                  const dayPeriods = periodsByDay[dateStr] || [];
                  const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                  const isLoadingDay = isLoading && dayPeriods.length === 0;
                  const hasNoPeriods = !isLoadingDay && dayPeriods.length === 0;

                  return (
                    <div
                      key={dayIndex}
                      className={cn("h-[130px] rounded border p-2", !isCurrentMonth && "bg-gray-50 opacity-30", hasNoPeriods ? "bg-gray-50" : "bg-white")}
                    >
                      <div
                        className={cn("mb-2 text-center text-sm font-medium", hasNoPeriods && "relative text-gray-400")}
                        style={hasNoPeriods ? { textDecorationColor: "#9ca3af" } : {}}
                      >
                        {day.getDate()}
                      </div>

                      {/* Conteneur avec hauteur fixe et grille pour l'alignement */}
                      <div className="flex h-[calc(100%-2rem)] flex-col justify-center gap-1">
                        {isLoadingDay ? (
                          // Skeleton pour les jours en cours de chargement
                          <>
                            <Skeleton className="h-full w-full rounded" />
                            <Skeleton className="h-full w-full rounded" />
                            <Skeleton className="h-full w-full rounded" />
                          </>
                        ) : (
                          // Affichage des périodes avec alignement correct en utilisant une grille
                          (["matin", "midi", "apres-midi"] as PeriodEnum[]).map((periodType) => {
                            const period = dayPeriods.find((p) => p.period === periodType);

                            if (!period) {
                              // Élément fantôme invisible pour maintenir l'alignement
                              return <div key={periodType} className="invisible h-6"></div>;
                            }

                            const selected = isPeriodSelected(childId, period.id);
                            const blocked = identicalForAll
                              ? selectedChildren.some((c) => reservedLookup[c.id]?.has(period.id))
                              : !!reservedLookup[childId]?.has(period.id);
                            const registrationIdBlocked = blocked
                              ? identicalForAll
                                ? (() => {
                                    for (const c of selectedChildren) {
                                      const reg = reservedLookup[c.id]?.get(period.id);
                                      if (reg) return reg;
                                    }
                                    return undefined;
                                  })()
                                : reservedLookup[childId]?.get(period.id)
                              : undefined;
                            const blockedChildren = identicalForAll
                              ? selectedChildren.filter((c) => reservedLookup[c.id]?.has(period.id))
                              : selectedChildren.filter((c) => c.id === childId && reservedLookup[c.id]?.has(period.id));
                            const childNamesList = blockedChildren.map((c) => `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim()).filter(Boolean);
                            const childNamesFormatted = childNamesList.length > 0 ? formatChildNamesWithEt(childNamesList) : "";
                            const message = (() => {
                              if (!childNamesFormatted) {
                                return identicalForAll ? "Un des enfants est déjà inscrit sur cette période" : "Cet enfant est déjà inscrit sur cette période";
                              }

                              console.log(blockedChildren);
                              return blockedChildren.length > 1
                                ? `${childNamesFormatted} sont déjà inscrits sur cette période`
                                : `${childNamesFormatted} est déjà inscrit${blockedChildren[0].gender === "M" ? "" : "e"} sur cette période`;
                            })();
                            return (
                              <TooltipProvider key={`${dateStr}-${periodType}`}>
                                <Tooltip disableHoverableContent={!blocked}>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      onClick={() => {
                                        if (blocked) return;
                                        handlePeriodToggle(childId, period.id);
                                      }}
                                      variant={selected ? "default" : "outline"}
                                      size="sm"
                                      className={cn(
                                        "h-6 px-1 py-1 text-xs",
                                        selected && "border-green-300 bg-green-100 text-green-700 hover:bg-green-200",
                                        blocked && "cursor-not-allowed! border-none bg-red-100 text-red-700 hover:bg-red-100 hover:text-red-700"
                                      )}
                                    >
                                      {periodType === "matin" && "Matin"}
                                      {periodType === "midi" && "Midi"}
                                      {periodType === "apres-midi" && "Aprèm"}
                                    </Button>
                                  </TooltipTrigger>
                                  {blocked && (
                                    <TooltipContent side="top" className="w-48 text-center">
                                      <p className="mb-1 text-xs font-medium">
                                        {childNamesFormatted ? (
                                          <>
                                            <em>{childNamesFormatted}</em>
                                            {blockedChildren.length > 1
                                              ? " sont déjà inscrits"
                                              : ` est déjà inscrit${blockedChildren[0].gender === "M" ? "" : "e"} sur cette période`}
                                          </>
                                        ) : (
                                          message
                                        )}
                                      </p>
                                      {registrationIdBlocked && (
                                        <a
                                          href={`/dash/registrations/${registrationIdBlocked}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center justify-center gap-1 text-xs text-red-400 hover:underline"
                                        >
                                          Voir la fiche
                                          <ExternalLink className="size-3" />
                                        </a>
                                      )}
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};
